import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Search, Package, History, AlertTriangle, X, RefreshCw,
  Coins, Crown, TrendingUp, TrendingDown, ListTree,
} from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/**
 * /console/inventory , Master Tuber Inventory + HQ Build-a-Box pool +
 * Audit Log. Feb 24 2026 rebuild (Phase 4 of Heritage Console).
 *
 * Three tabs:
 *   1. Master , every published variety with physical stock, all 4 prices,
 *      damaged/lost/holding/replacement counts. Click any field to edit;
 *      every change writes an audit row.
 *   2. HQ Pool , the 4-per-variety customer-facing pool, decoupled from
 *      master. Set 0 to hide a variety from HQ Build-a-Box. Capped at 4.
 *   3. Audit Log , full movement history with reason + actor.
 */

const TABS = [
  { key: "master", label: "Master Tuber Inventory", Icon: Package },
  { key: "hq",     label: "HQ Build-a-Box Pool",    Icon: Crown },
  { key: "audit",  label: "Stock Audit Log",        Icon: ListTree },
];

export default function ConsoleInventory() {
  const [tab, setTab] = useState("master");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stockFilter, setStockFilter] = useState("all"); // all | low | out | in

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/inventory", { params: { q } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error("Couldn't load inventory."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/console/inventory");
        if (!cancelled) setRows(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) toast.error("Couldn't load inventory.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = useMemo(() => ({
    all: rows.length,
    in: rows.filter((r) => !r.out_of_stock).length,
    low: rows.filter((r) => r.low_stock && !r.out_of_stock).length,
    out: rows.filter((r) => r.out_of_stock).length,
    total_tubers: rows.reduce((s, r) => s + (r.qty_available || 0), 0),
    hq_pool: rows.reduce((s, r) => s + (r.hq_pool_quantity || 0), 0),
    damaged: rows.reduce((s, r) => s + (r.damaged_count || 0), 0),
    lost:    rows.reduce((s, r) => s + (r.lost_count || 0), 0),
    holding: rows.reduce((s, r) => s + (r.holding_count || 0), 0),
    inventory_value: rows.reduce((s, r) => s + (Number(r.qty_available) * Number(r.cost_price || 0)), 0),
  }), [rows]);

  const filtered = useMemo(() => {
    let xs = rows;
    if (q.trim()) {
      const rx = q.trim().toLowerCase();
      xs = xs.filter((v) =>
        (v.name || "").toLowerCase().includes(rx) ||
        (v.slug || "").toLowerCase().includes(rx) ||
        (v.sku  || "").toLowerCase().includes(rx),
      );
    }
    if (stockFilter === "low") xs = xs.filter((r) => r.low_stock && !r.out_of_stock);
    if (stockFilter === "out") xs = xs.filter((r) => r.out_of_stock);
    if (stockFilter === "in")  xs = xs.filter((r) => !r.out_of_stock);
    return xs;
  }, [rows, q, stockFilter]);

  const patchMaster = async (variety, patch) => {
    // Side-channel: when QtyAdjust succeeds it sends {__qty_only: N} so we
    // just update the row's qty without making a second API call.
    if (patch && Object.prototype.hasOwnProperty.call(patch, "__qty_only")) {
      const newQty = patch.__qty_only;
      setRows((prev) => prev.map((v) => v.id === variety.id ? {
        ...v, qty_available: newQty,
        low_stock: newQty <= 2, out_of_stock: newQty <= 0,
      } : v));
      return;
    }
    try {
      const r = await api.patch(`/console/inventory/${variety.id}/master`, patch);
      const refreshed = r.data?.stock || {};
      setRows((prev) => prev.map((v) => v.id === variety.id ? {
        ...v,
        ...Object.fromEntries(Object.entries(patch).filter(([k]) => k !== "reason")),
        ...refreshed,
        // Recompute derived helpers
        low_stock: (refreshed.qty_available ?? v.qty_available) <= 2,
        out_of_stock: (refreshed.qty_available ?? v.qty_available) <= 0,
      } : v));
      toast.success(`${variety.name} updated.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    }
  };

  return (
    <div data-testid="console-inventory">
      <header className="mb-5">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">Heritage Dahlias · Console</div>
        <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1 flex items-center gap-3">
          <Package className="w-7 h-7 text-[#6E1E3A]" /> Inventory & Stock
        </h1>
        <p className="text-[#7A6A55] mt-2 max-w-2xl text-sm">
          Master tuber inventory is what you have in the shed. HQ Build-a-Box is the customer-facing pool, decoupled from master and never shown for public sale.
        </p>
      </header>

      {/* Tab bar */}
      <div className="mb-5 flex gap-2 border-b border-[#E8DFCB]">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`inv-tab-${key}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase font-semibold border-b-2 -mb-px transition ${
              tab === key
                ? "border-[#6E1E3A] text-[#6E1E3A]"
                : "border-transparent text-[#7A6A55] hover:text-[#3C2A1E]"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Stat tiles common to Master + HQ tab */}
      {tab !== "audit" && (
        <section className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
          <Tile label="All varieties"      value={totals.all} active={stockFilter === "all"} onClick={() => setStockFilter("all")} testid="inv-tile-all" />
          <Tile label="In stock"          value={totals.in}  tone="forest" active={stockFilter === "in"} onClick={() => setStockFilter("in")} testid="inv-tile-in" />
          <Tile label="Low (≤ 2)"         value={totals.low} tone="amber"  active={stockFilter === "low"} onClick={() => setStockFilter("low")} testid="inv-tile-low" />
          <Tile label="Out of stock"      value={totals.out} tone="red"    active={stockFilter === "out"} onClick={() => setStockFilter("out")} testid="inv-tile-out" />
          <Tile label="Total tubers"      value={totals.total_tubers} tone="berry" />
          <Tile label="HQ pool tubers"    value={totals.hq_pool}      tone="berry" />
        </section>
      )}

      {tab === "master" && (
        <MasterTab
          rows={filtered} loading={loading}
          q={q} setQ={setQ}
          onPatch={patchMaster}
          onBulkDone={load}
          onSyncCatalog={async () => {
            if (!window.confirm("Sync inventory with the catalog?\nThis adds a 0-stock row for every published variety that doesn't have one yet. Existing counts are not touched.")) return;
            try {
              const r = await api.post("/console/inventory/sync-catalog");
              toast.success(`Synced. ${r.data.created} new row(s), ${r.data.already_synced} already had stock.`);
              await load();
            } catch { toast.error("Sync failed."); }
          }}
        />
      )}
      {tab === "hq" && (
        <HqPoolTab rows={filtered} loading={loading} q={q} setQ={setQ} onPatch={patchMaster} />
      )}
      {tab === "audit" && (
        <AuditTab />
      )}
    </div>
  );
}

/* ============================ Master tab ============================== */

function MasterTab({ rows, loading, q, setQ, onPatch, onSyncCatalog, onBulkDone }) {
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAmount, setBulkAmount] = useState(10);
  const [bulkTarget, setBulkTarget] = useState("hq_pool");
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allVisibleSelected = rows.length > 0 && rows.every((v) => selected.has(v.id));
  const toggleAll = () => setSelected(allVisibleSelected ? new Set() : new Set(rows.map((v) => v.id)));

  const applyBulk = async () => {
    const n = parseInt(bulkAmount, 10);
    if (!Number.isFinite(n) || n === 0) { toast.error("Enter a non-zero amount."); return; }
    setBulkBusy(true);
    try {
      const r = await api.post("/console/inventory/bulk-adjust", {
        variety_ids: [...selected],
        amount: n,
        target: bulkTarget,
        mode: "add",
        reason: `Bulk add ${n} each (${bulkTarget === "hq_pool" ? "Build-a-Box pool" : "master stock"})`,
      });
      toast.success(`Added ${n} to ${r.data.updated} variet${r.data.updated === 1 ? "y" : "ies"} (${bulkTarget === "hq_pool" ? "Build-a-Box pool" : "master stock"}).`);
      setSelected(new Set());
      if (onBulkDone) await onBulkDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed.");
    } finally { setBulkBusy(false); }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E8DFCB] rounded max-w-md flex-1">
          <Search className="w-4 h-4 text-[#9C8B73]" />
          <input
            type="text" placeholder="Search name, slug, sku…"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="inventory-search"
            className="flex-1 outline-none text-sm bg-transparent"
          />
        </div>
        <button
          type="button" onClick={onSyncCatalog}
          data-testid="inventory-sync"
          className="px-4 py-2 bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Sync catalog
        </button>
      </div>

      {/* Bulk action bar */}
      <div className="mb-3 sticky top-0 z-10 bg-[#F8F4EA] border border-[#E8DFCB] rounded-md p-3 flex flex-wrap items-center gap-3" data-testid="inv-bulk-bar">
        <label className="inline-flex items-center gap-2 text-xs text-[#3C2A1E] font-semibold">
          <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="inv-bulk-select-all" />
          Select all ({rows.length})
        </label>
        <span className="text-xs text-[#6E1E3A] font-semibold" data-testid="inv-bulk-count">{selected.size} selected</span>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#7A6A55]">Add</span>
          <input
            type="number" inputMode="numeric" step="1"
            value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)}
            data-testid="inv-bulk-amount"
            className="w-20 px-2 py-1.5 text-sm bg-white border border-[#E8DFCB] rounded text-center"
          />
          <span className="text-xs text-[#7A6A55]">each to</span>
          <select value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)} data-testid="inv-bulk-target"
            className="px-2 py-1.5 text-xs bg-white border border-[#E8DFCB] rounded">
            <option value="hq_pool">HQ Build-a-Box pool (not public)</option>
            <option value="master">Master stock (shed)</option>
          </select>
          <button
            type="button" onClick={applyBulk} disabled={bulkBusy || selected.size === 0}
            data-testid="inv-bulk-apply"
            className="px-4 py-1.5 bg-[#2F4A2A] hover:bg-[#243B20] text-white rounded text-[10px] tracking-[0.25em] uppercase font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />} Apply to {selected.size}
          </button>
          {selected.size > 0 && (
            <button type="button" onClick={() => setSelected(new Set())} data-testid="inv-bulk-clear"
              className="text-[10px] tracking-[0.2em] uppercase text-[#7A6A55] hover:text-[#3C2A1E] underline">
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <Empty />
      ) : (
        <div className="bg-white border border-[#E8DFCB] rounded-md overflow-hidden">
          {rows.map((v) => (
            <MasterRow
              key={v.id} v={v}
              isOpen={openId === v.id}
              onToggle={() => setOpenId(openId === v.id ? null : v.id)}
              onPatch={(patch) => onPatch(v, patch)}
              selected={selected.has(v.id)}
              onSelect={() => toggleSelect(v.id)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function MasterRow({ v, isOpen, onToggle, onPatch, selected, onSelect }) {
  return (
    <div className={`border-b border-[#F4ECD8] last:border-b-0 ${selected ? "bg-[#6E1E3A]/5" : ""}`} data-testid={`inv-row-${v.id}`}>
      <div className="flex items-center">
        <label className="pl-3 py-3 cursor-pointer" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={!!selected} onChange={onSelect} data-testid={`inv-select-${v.id}`} className="w-4 h-4 accent-[#6E1E3A]" />
        </label>
        <button
          type="button" onClick={onToggle}
          className="flex-1 text-left p-3 grid grid-cols-12 gap-3 items-center hover:bg-[#FBF6E9] transition"
        >
        <div className="col-span-12 md:col-span-5 flex items-center gap-3">
          {v.image ? (
            <img src={v.image} alt={v.name} loading="lazy" className="w-12 h-12 object-cover rounded border border-[#E8DFCB]" />
          ) : (
            <div className="w-12 h-12 rounded bg-gradient-to-br from-[#F4C8D2] via-[#C9508A] to-[#6E1E3A] grid place-items-center text-white">
              <Package className="w-5 h-5" />
            </div>
          )}
          <div>
            <div className="font-serif text-base text-[#3C2A1E]">{v.name}</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">
              {v.slug}{v.sku ? ` · ${v.sku}` : ""}
            </div>
          </div>
        </div>
        <div className="col-span-3 md:col-span-2 text-center">
          <div className={`font-serif text-2xl ${v.out_of_stock ? "text-[#A33030]" : v.low_stock ? "text-[#C49247]" : "text-[#3C2A1E]"}`} data-testid={`inv-master-qty-${v.id}`}>
            {v.qty_available}
          </div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#9C8B73]">Master</div>
        </div>
        <div className="col-span-3 md:col-span-2 text-center">
          <div className="font-serif text-2xl text-[#6E1E3A]" data-testid={`inv-hq-pool-${v.id}`}>{v.hq_pool_quantity || 0}</div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#9C8B73]">HQ pool</div>
        </div>
        <div className="col-span-3 md:col-span-2 text-center">
          <div className="font-serif text-lg text-[#3C2A1E]">${Number(v.retail_price || v.price || 0).toFixed(2)}</div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#9C8B73]">Retail</div>
        </div>
        <div className="col-span-3 md:col-span-1 text-right">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#6E1E3A]">{isOpen ? "Close" : "Edit"}</span>
        </div>
        </button>
      </div>
      {isOpen && (
        <div className="p-4 bg-[#FBF6E9] border-t border-[#E8DFCB] grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`inv-master-edit-${v.id}`}>
          {/* Prices */}
          <Panel title="Pricing">
            <Money label="Cost price"        value={v.cost_price}        onSave={(n) => onPatch({ cost_price: n })}        testid={`inv-cost-${v.id}`} />
            <Money label="Retail price"      value={v.retail_price}      onSave={(n) => onPatch({ retail_price: n })}      testid={`inv-retail-${v.id}`} />
            <Money label="VIP price"         value={v.vip_price}         onSave={(n) => onPatch({ vip_price: n })}         testid={`inv-vip-${v.id}`} />
            <Money label="Mystery value"     value={v.mystery_value}     onSave={(n) => onPatch({ mystery_value: n })}     testid={`inv-mystery-${v.id}`} />
            <Money label="Replacement value" value={v.replacement_value} onSave={(n) => onPatch({ replacement_value: n })} testid={`inv-replacement-${v.id}`} />
          </Panel>
          {/* Stock buckets */}
          <Panel title="Stock buckets">
            <Counter label="Damaged"     value={v.damaged_count}     onSave={(n) => onPatch({ damaged_count: n })}     testid={`inv-damaged-${v.id}`} />
            <Counter label="Lost"        value={v.lost_count}        onSave={(n) => onPatch({ lost_count: n })}        testid={`inv-lost-${v.id}`} />
            <Counter label="Holding"     value={v.holding_count}     onSave={(n) => onPatch({ holding_count: n })}     testid={`inv-holding-${v.id}`} />
            <Counter label="Replacement" value={v.replacement_count} onSave={(n) => onPatch({ replacement_count: n })} testid={`inv-replacement-count-${v.id}`} />
          </Panel>
          {/* Quick qty adjust */}
          <Panel title="Adjust master qty">
            <QtyAdjust v={v} onUpdated={(newQty) => onPatch({ __qty_only: newQty })} />
          </Panel>
        </div>
      )}
    </div>
  );
}

function QtyAdjust({ v, onUpdated }) {
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState("");
  const [type, setType] = useState("add");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = parseInt(delta, 10);
    if (!Number.isFinite(n) || n === 0) { toast.error("Enter a non-zero number."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/console/inventory/${v.id}/adjust`, {
        delta: n, reason: reason || `${type} (console)`, adjustment_type: type,
      });
      if (onUpdated) onUpdated(r.data.qty_available);
      toast.success(`${v.name}: ${n > 0 ? "+" : ""}${n} (now ${r.data.qty_available}).`);
      setDelta(1); setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't adjust.");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid={`inv-adj-type-${v.id}`} className="px-2 py-1.5 text-xs bg-white border border-[#E8DFCB] rounded">
          <option value="add">Add stock</option>
          <option value="remove">Remove stock</option>
          <option value="damaged">Damaged</option>
          <option value="lost">Lost</option>
          <option value="returned">Returned</option>
          <option value="sold">Sold</option>
          <option value="reserved">Reserved</option>
          <option value="released">Released</option>
          <option value="manual">Manual correction</option>
        </select>
        <input
          type="number" inputMode="numeric" step="1"
          value={delta} onChange={(e) => setDelta(e.target.value)}
          placeholder="±N"
          data-testid={`inv-adj-delta-${v.id}`}
          className="px-2 py-1.5 text-xs bg-white border border-[#E8DFCB] rounded"
        />
      </div>
      <input
        type="text" value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional)"
        data-testid={`inv-adj-reason-${v.id}`}
        className="w-full px-2 py-1.5 text-xs bg-white border border-[#E8DFCB] rounded"
      />
      <button
        type="button" onClick={submit} disabled={busy}
        data-testid={`inv-adj-go-${v.id}`}
        className="w-full px-3 py-1.5 bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded text-[10px] tracking-[0.25em] uppercase font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (parseInt(delta, 10) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
        Apply adjustment
      </button>
    </div>
  );
}

/* ============================ HQ Pool tab ============================ */

function HqPoolTab({ rows, loading, q, setQ, onPatch }) {
  const visible = rows.filter((v) => v.qty_available > 0 || v.hq_pool_quantity > 0);
  return (
    <>
      <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-white border border-[#E8DFCB] rounded max-w-md">
        <Search className="w-4 h-4 text-[#9C8B73]" />
        <input
          type="text" placeholder="Search name, slug, sku…"
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="hq-pool-search"
          className="flex-1 outline-none text-sm bg-transparent"
        />
      </div>
      <div className="mb-3 px-3 py-2 bg-[#6E1E3A]/8 border border-[#6E1E3A]/20 rounded text-xs text-[#6E1E3A]">
        Allocate any number of tubers per variety to the customer-facing HQ Build-a-Box pool (separate from public sale stock). Set to 0 to hide a variety from VIP Build-a-Box entirely. Tip: use the Master tab's bulk bar to add e.g. 10 to many varieties at once.
      </div>
      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : visible.length === 0 ? (
        <Empty />
      ) : (
        <div className="bg-white border border-[#E8DFCB] rounded grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8DFCB]">
          {visible.map((v) => (
            <HqPoolCard key={v.id} v={v} onPatch={(n) => onPatch(v, { hq_pool_quantity: n })} />
          ))}
        </div>
      )}
    </>
  );
}

function HqPoolCard({ v, onPatch }) {
  const [custom, setCustom] = useState("");
  const set = (n) => onPatch(Math.max(0, parseInt(n, 10) || 0));
  return (
    <div className="p-3 bg-white" data-testid={`hq-pool-card-${v.id}`}>
      <div className="flex items-center gap-3">
        {v.image ? (
          <img src={v.image} alt={v.name} className="w-10 h-10 object-cover rounded border border-[#E8DFCB]" />
        ) : (
          <div className="w-10 h-10 rounded bg-[#FBF6E9] grid place-items-center text-[#6E1E3A]"><Package className="w-4 h-4" /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-serif text-sm text-[#3C2A1E] truncate">{v.name}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#9C8B73]">Master: {v.qty_available} · Pool: {v.hq_pool_quantity || 0}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        {[0, 1, 2, 3, 4].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => set(n)}
            data-testid={`hq-pool-set-${v.id}-${n}`}
            className={`flex-1 h-8 text-xs font-mono font-semibold rounded ${
              (v.hq_pool_quantity || 0) === n
                ? "bg-[#6E1E3A] text-white"
                : "bg-[#FBF6E9] text-[#6E1E3A] hover:bg-[#6E1E3A]/15"
            }`}
          >
            {n}
          </button>
        ))}
        <input
          type="number" inputMode="numeric" min="0" step="1"
          value={custom} onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && custom !== "") { set(custom); setCustom(""); } }}
          onBlur={() => { if (custom !== "") { set(custom); setCustom(""); } }}
          placeholder="N"
          title="Set any amount (Enter to apply)"
          data-testid={`hq-pool-custom-${v.id}`}
          className={`w-14 h-8 text-xs font-mono font-semibold rounded text-center border border-[#E8DFCB] ${
            (v.hq_pool_quantity || 0) > 4 ? "bg-[#6E1E3A] text-white placeholder-white/60" : "bg-white text-[#6E1E3A]"
          }`}
        />
      </div>
    </div>
  );
}

/* ============================ Audit Log tab ============================ */

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/console/inventory/movements", { params: { limit: 500 } });
        if (!cancelled) setRows(Array.isArray(r.data) ? r.data : []);
      } catch { if (!cancelled) toast.error("Couldn't load audit log."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (filterKind === "all") return rows;
    return rows.filter((m) => (m.kind || (m.field ? "master-field-update" : "qty-adjustment")) === filterKind);
  }, [rows, filterKind]);

  return (
    <div data-testid="inv-audit-tab">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Filter:</div>
        {[
          { v: "all", l: "All" },
          { v: "qty-adjustment", l: "Qty adjustments" },
          { v: "master-field-update", l: "Field updates" },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setFilterKind(opt.v)}
            data-testid={`inv-audit-filter-${opt.v}`}
            className={`px-3 py-1 text-[10px] tracking-[0.25em] uppercase font-semibold rounded ${
              filterKind === opt.v ? "bg-[#6E1E3A] text-white" : "bg-white border border-[#E8DFCB] text-[#3C2A1E] hover:bg-[#FBF6E9]"
            }`}
          >
            {opt.l}
          </button>
        ))}
        <div className="ml-auto text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">
          {filtered.length} of {rows.length} entries
        </div>
      </div>
      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8DFCB] p-10 text-center text-[#7A6A55] rounded text-sm">
          No audit entries yet. Adjust some stock or update a field to see them here.
        </div>
      ) : (
        <div className="bg-white border border-[#E8DFCB] rounded divide-y divide-[#F4ECD8]">
          {filtered.map((m) => {
            const kind = m.kind || (m.field ? "master-field-update" : "qty-adjustment");
            const isField = kind === "master-field-update";
            return (
              <div key={m.id} className="p-3 grid grid-cols-12 gap-3 items-center text-xs" data-testid={`audit-${m.id}`}>
                <div className="col-span-12 md:col-span-3 font-serif text-sm text-[#3C2A1E]">
                  {m.variety_name || m.variety_id?.slice(0, 8)}
                </div>
                <div className="col-span-6 md:col-span-2 text-[10px] tracking-[0.25em] uppercase">
                  {isField ? (
                    <span className="text-[#C49247]">{m.field}</span>
                  ) : (
                    <span className="text-[#6E1E3A]">{m.adjustment_type || "manual"}</span>
                  )}
                </div>
                <div className={`col-span-6 md:col-span-2 font-mono font-semibold ${
                  isField ? "text-[#7A6A55]" : Number(m.delta) > 0 ? "text-[#2F4A2A]" : "text-[#A33030]"
                }`}>
                  {isField
                    ? `${m.before} → ${m.after}`
                    : `${Number(m.delta) > 0 ? "+" : ""}${m.delta} (=${m.balance_after})`}
                </div>
                <div className="col-span-12 md:col-span-3 italic text-[#7A6A55] truncate">
                  {m.reason || "-"}
                </div>
                <div className="col-span-12 md:col-span-2 text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] text-right">
                  {new Date(m.created_at).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  <div className="normal-case tracking-normal text-[#7A6A55] truncate" title={m.actor}>{m.actor || "console"}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================ Shared bits ============================= */

function Panel({ title, children }) {
  return (
    <div className="bg-white border border-[#E8DFCB] rounded p-3">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E1E3A] font-semibold mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Money({ label, value, onSave, testid }) {
  const initial = Number(value || 0).toFixed(2);
  const [v, setV] = useState(initial);
  // Reset local edit when the saved value changes externally.
  const valueKey = String(value || 0);
  const [lastSeenValue, setLastSeenValue] = useState(valueKey);
  if (lastSeenValue !== valueKey) {
    setLastSeenValue(valueKey);
    setV(Number(value || 0).toFixed(2));
  }
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-2">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">{label}</div>
      <div className="relative">
        <Coins className="w-3 h-3 text-[#9C8B73] absolute left-2 top-1/2 -translate-y-1/2" />
        <input
          type="number" step="0.01" min="0"
          value={v} onChange={(e) => setV(e.target.value)}
          onBlur={() => { const n = Math.max(0, parseFloat(v) || 0); if (n.toFixed(2) !== Number(value || 0).toFixed(2)) onSave(n); }}
          data-testid={testid}
          className="w-full pl-7 pr-2 py-1.5 text-xs bg-[#FBF6E9] border border-[#E8DFCB] rounded text-right"
        />
      </div>
    </div>
  );
}

function Counter({ label, value, onSave, testid }) {
  const [v, setV] = useState(String(value || 0));
  const valueKey = String(value || 0);
  const [lastSeenValue, setLastSeenValue] = useState(valueKey);
  if (lastSeenValue !== valueKey) {
    setLastSeenValue(valueKey);
    setV(String(value || 0));
  }
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-2">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">{label}</div>
      <input
        type="number" min="0" step="1"
        value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => { const n = Math.max(0, parseInt(v, 10) || 0); if (n !== Number(value || 0)) onSave(n); }}
        data-testid={testid}
        className="w-full px-2 py-1.5 text-xs bg-[#FBF6E9] border border-[#E8DFCB] rounded text-right"
      />
    </div>
  );
}

function Tile({ label, value, tone = "muted", active = false, onClick, testid }) {
  const palette = {
    muted:  active ? "bg-[#3C2A1E] text-white" : "bg-white border border-[#E8DFCB] text-[#3C2A1E] hover:bg-[#FBF6E9]",
    forest: active ? "bg-[#2F4A2A] text-white" : "bg-[#2F4A2A]/10 border border-[#2F4A2A]/30 text-[#2F4A2A] hover:bg-[#2F4A2A]/20",
    amber:  active ? "bg-[#C49247] text-white" : "bg-[#C49247]/10 border border-[#C49247]/30 text-[#C49247] hover:bg-[#C49247]/20",
    red:    active ? "bg-[#A33030] text-white" : "bg-[#A33030]/10 border border-[#A33030]/30 text-[#A33030] hover:bg-[#A33030]/20",
    berry:  "bg-[#6E1E3A] text-white",
  }[tone];
  const clickable = !!onClick;
  return (
    <button type="button" onClick={onClick} disabled={!clickable} data-testid={testid}
      className={`${palette} p-3 rounded-md text-left transition ${clickable ? "cursor-pointer" : "cursor-default"}`}>
      <div className="text-[9px] tracking-[0.3em] uppercase opacity-85 font-semibold">{label}</div>
      <div className="font-serif text-2xl mt-1 leading-none">{value}</div>
    </button>
  );
}

function Empty() {
  return (
    <div className="bg-white border border-dashed border-[#E8DFCB] p-10 text-center text-[#7A6A55] rounded">
      No varieties match. Try a different search or sync the catalog.
    </div>
  );
}
